import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import * as adminApi from '../../../api/adminApi';
import { S } from './adminPageStyles';

/** Plan × feature matrix (SRS #2): what each plan advertises vs what is enforced. */

const AdminPlanMatrixPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const token = window.localStorage.getItem('policyora_token') ?? '';
  const [m, setM] = useState<adminApi.PlanMatrixView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { adminApi.getPlanMatrix(token).then(setM).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load')); }, [token]);

  const cell = (adv: boolean, enf: boolean) => {
    if (adv && enf) return { text: '✓', bg: 'rgba(2,122,72,0.10)', color: 'var(--ds-success-text)', title: 'Advertised and enforced' };
    if (!adv && !enf) return { text: '—', bg: 'transparent', color: 'var(--ds-text-faint)', title: 'Not included' };
    if (adv && !enf) return { text: 'sold, not enforced', bg: 'rgba(240,68,56,0.10)', color: 'var(--ds-danger-text)', title: 'Advertised on the pricing page but NOT enforced' };
    return { text: 'enforced, not sold', bg: 'rgba(247,144,9,0.14)', color: 'var(--ds-warning-text)', title: 'Enforced but not shown on the pricing page' };
  };

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="Plan Matrix" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <h2 style={S.h2}>Plan × feature matrix</h2>
        <p style={S.sub}>What each plan advertises on the pricing page against what the platform actually enforces. Any cell that is not ✓ or — is a mismatch a customer could notice.</p>
        {error && <div style={S.error}>{error}</div>}
        {m && (
          <>
            <div style={{ overflowX: 'auto', border: '1px solid var(--ds-border)', borderRadius: 8 }}>
              <table style={S.table}>
                <thead><tr><th style={S.th}>Plan</th>{m.quantity_fields.map((qf) => <th key={qf} style={S.th}>{qf.replace(/_/g, ' ')}</th>)}{m.features.map((f) => <th key={f} style={S.th}>{f.replace(/_/g, ' ')}</th>)}</tr></thead>
                <tbody>{m.plans.map((p) => (
                  <tr key={p.tier} style={p.disabled ? { opacity: 0.5 } : undefined}>
                    <td style={{ ...S.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{p.display_name}{p.disabled && <span style={{ ...S.chip, marginLeft: 6 }}>disabled</span>}</td>
                    {m.quantity_fields.map((qf) => <td key={qf} style={{ ...S.td, ...S.mono }}>{p.quantities[qf] === null || p.quantities[qf] === undefined ? '—' : String(p.quantities[qf])}</td>)}
                    {m.features.map((f) => { const c = cell(!!p.advertised[f], !!p.enforced[f]); return <td key={f} title={c.title} style={{ ...S.td, background: c.bg, color: c.color, textAlign: 'center', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{c.text}</td>; })}
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {m.issues && m.issues.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h3 style={{ margin: '0 0 6px', fontSize: '0.95rem' }}>Inconsistencies ({m.issues.length})</h3>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.84rem', color: 'var(--ds-text-secondary)' }}>
                  {m.issues.map((i, idx) => <li key={idx}><span style={S.chip}>{i.kind}</span> {i.detail}</li>)}
                </ul>
              </div>
            )}
          </>
        )}
      </section>
    </DashboardLayout>
  );
};

export default AdminPlanMatrixPage;
